import {memo} from 'react'
import {NOTIFICATION_TYPE, UserNotification} from '@/features/notifications/types'
import {
    StyledMarkReadButton,
    StyledNotificationItem,
    StyledNotificationTime,
    StyledNotificationUnreadDot
} from './style'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'
import {Avatar} from '@/components/ui/avatar/Avatar'
import {formatNotificationDate} from '@/features/notifications/utils/dateFormatters'
import {useLocation, useNavigate} from 'react-router-dom'
import {CheckIcon, RefreshCcw05Icon} from '@/components/ui/icon/Icon'
import {useMarkNotification} from '../../hooks/useMarkNotification'
import {useNotificationDescription} from '../../hooks/useNotificationDescription'
import {useTranslation} from '@/translations/i18n'
import {ConversationKindBadge} from '@/features/conversations/components/conversation-kind-badge/ConversationKindBadge'
import {useInternalConversationsStore} from '@/features/conversations/store/useInternalConversationsStore'
import {INTERNAL_CONVERSATION_ID_PARAM} from '@/features/conversations/components/internal-conversations-provider/InternalConversationsProvider'
import {NOTIFICATION_ID_PARAM} from '../../hooks/useNotificationWorkContext'

interface NotificationItemProps {
    notification: UserNotification
    onClick?: (notification: UserNotification) => void
}

const NotificationItemComponent = ({notification, onClick}: NotificationItemProps) => {
    const {t} = useTranslation()
    const {mutate: markNotification} = useMarkNotification({urlParams: {notificationId: notification.id}})
    const navigate = useNavigate()
    const location = useLocation()
    const openInternalConversations = useInternalConversationsStore(state => state.open)

    const {type, severity, sentAt, extraData, link} = notification.notification
    const {icon, senderName, title, conversationKind, conversationTitleLabel, conversationTitle, subtitle} =
        useNotificationDescription(notification)

    const formattedTime = formatNotificationDate(sentAt)

    const handleClick = () => {
        // Opening a notification performs no state transition: a task stays pending until
        // the operator explicitly declares it handled, here or from the work-context bar.
        if (onClick) {
            onClick(notification)
        }

        if (link) {
            const url = new URL(link)
            // Added on top of the destination's own params — conversation deep links already
            // carry `conversation_id` and must keep it.
            url.searchParams.set(NOTIFICATION_ID_PARAM, notification.id)
            navigate(`${url.pathname}${url.search}${url.hash}`)
        } else if (type === NOTIFICATION_TYPE['Conv-001'] || type === NOTIFICATION_TYPE['Conv-002']) {
            // Conversation without a patient: the BE sends no link, so the destination is the
            // internal conversations drawer on the current page. Going through the address
            // rather than the store means this case carries a work context too —
            // InternalConversationsProvider already opens the drawer from the param.
            const conversationId = extraData?.conversationId ?? notification.conversationId ?? null
            const searchParams = new URLSearchParams(location.search)
            searchParams.set(NOTIFICATION_ID_PARAM, notification.id)
            if (conversationId) {
                searchParams.set(INTERNAL_CONVERSATION_ID_PARAM, conversationId)
            } else {
                // Older notifications carry no conversation id: there is nothing to deep-link
                // to, so open the drawer on its list as before.
                openInternalConversations()
            }
            navigate({pathname: location.pathname, search: searchParams.toString()})
        }
    }

    return (
        <StyledNotificationItem
            $isAlert={severity === 'alert'}
            direction="row"
            align="start"
            justify="space-between"
            fullWidth
            gap={4}
            onClick={handleClick}
        >
            <Flexbox direction="column" align="start" gap={2}>
                <Flexbox direction="row" align="start" gap={2}>
                    {!notification.readAt && <StyledNotificationUnreadDot />}
                    <Flexbox direction="column" align="start" gap={2}>
                        {senderName && (
                            <Flexbox direction="row" align="center" gap={2}>
                                <Avatar name={senderName} size="xs" />
                                <Text size="textXs" palette="neutral" shade="700">
                                    {senderName}
                                </Text>
                            </Flexbox>
                        )}
                        <Flexbox direction="column" gap={1}>
                            {/* The kind of conversation comes before anything else: operators
                                asked to recognise it without reading the title or the names. */}
                            {conversationKind && <ConversationKindBadge kind={conversationKind} />}
                            <Flexbox align="center" gap={1}>
                                {icon}
                                {conversationTitle ? (
                                    <Flexbox direction="column" align="start" gap={0}>
                                        <Text size="textXs" weight="500" palette="neutral" shade="900">
                                            {conversationTitleLabel}
                                        </Text>
                                        <Text size="textMd" weight="500" palette="neutral" shade="900">
                                            "{conversationTitle}"
                                        </Text>
                                    </Flexbox>
                                ) : (
                                    <Text size="textMd" weight="500" palette="neutral" shade="900">
                                        {title}
                                    </Text>
                                )}
                            </Flexbox>
                            {/* On a conversation the patient is part of its identity, not a
                                detail, so it is stated and given the weight of the title —
                                every other type keeps the muted extraData line. */}
                            {conversationKind === 'patient' && subtitle?.patientName ? (
                                <Text size="textSm" weight="500" palette="neutral" shade="900">
                                    {t('conversations:patient_label', {name: subtitle.patientName})}
                                </Text>
                            ) : (
                                subtitle && (
                                    <Text size="textXs" palette="neutral" shade="700">
                                        {subtitle.patientName && <span>{subtitle.patientName}</span>}
                                        {subtitle.projectCode && <span> • PRI n°{subtitle.projectCode}</span>}
                                        {subtitle.repeatedAgendaDetail && (
                                            <span> • {subtitle.repeatedAgendaDetail}</span>
                                        )}
                                    </Text>
                                )
                            )}
                        </Flexbox>
                    </Flexbox>
                </Flexbox>
            </Flexbox>
            <Flexbox direction="column" align="end" gap={1}>
                <StyledMarkReadButton
                    variant={notification.readAt ? 'secondary' : 'primary'}
                    size="xs"
                    shape="rectangle"
                    style={{whiteSpace: 'nowrap'}}
                    onClick={event => {
                        event.stopPropagation()
                        // Both directions go through the same reversible endpoint, so a handled
                        // task can always be reopened — `isRead` is always sent explicitly
                        // because the backend treats an omitted flag as "clear the state".
                        markNotification({isRead: !notification.readAt})
                    }}
                >
                    {/* Same icon language as the work-context bar: a check marks a task done,
                        a counter-clockwise arrow puts it back in the queue. */}
                    {notification.readAt ? <RefreshCcw05Icon /> : <CheckIcon />}
                    {notification.readAt ? t('notifications:reopen_task') : t('notifications:mark_as_read')}
                </StyledMarkReadButton>
                <StyledNotificationTime>{formattedTime}</StyledNotificationTime>
            </Flexbox>
        </StyledNotificationItem>
    )
}

export const NotificationItem = memo(NotificationItemComponent)
